import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Loader2, Download } from "lucide-react";
import { listPayments } from "@/lib/accounting/accounting.functions";
import { downloadCSV, fmtDate, fmtMoney, toCSV } from "@/lib/accounting/exports";

export const Route = createFileRoute("/_authenticated/admin/accounting/payments")({
  component: Payments,
});

function Payments() {
  const fn = useServerFn(listPayments);
  const { data, isLoading } = useQuery({ queryKey: ["accounting", "payments"], queryFn: () => fn() });

  const exportCsv = () => {
    const rows = (data ?? []).map((p: any) => ({
      date: p.recorded_at, customer: p.invoices?.customer_name ?? p.customer_projects?.customer_name,
      job: p.customer_projects?.project_name, invoice: p.invoices?.invoice_number,
      method: p.method, category: p.category ?? p.kind, amount: p.amount, reference: p.reference,
    }));
    downloadCSV(`payments_${new Date().toISOString().slice(0, 10)}.csv`, toCSV(rows));
  };

  if (isLoading) return <div className="py-20 grid place-items-center"><Loader2 className="size-6 animate-spin text-amber-300" /></div>;
  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <button onClick={exportCsv} className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-white/10 text-xs text-zinc-200 hover:bg-white/5"><Download className="size-3.5" /> CSV</button>
      </div>
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-x-auto">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500 border-b border-white/10">
            <tr><th className="text-left px-4 py-3">Date</th><th className="text-left px-4 py-3">Customer</th><th className="text-left px-4 py-3">Invoice</th><th className="text-left px-4 py-3">Method</th><th className="text-left px-4 py-3">Type</th><th className="text-right px-4 py-3">Amount</th><th className="text-left px-4 py-3">Reference</th></tr>
          </thead>
          <tbody>
            {(data ?? []).length === 0 ? (
              <tr><td colSpan={7} className="text-center py-12 text-zinc-500">No payments yet.</td></tr>
            ) : (data ?? []).map((p: any) => (
              <tr key={p.id} className="border-b border-white/5">
                <td className="px-4 py-3 text-zinc-300 text-xs">{fmtDate(p.recorded_at)}</td>
                <td className="px-4 py-3 text-white">{p.invoices?.customer_name ?? p.customer_projects?.customer_name ?? "—"}</td>
                <td className="px-4 py-3 text-amber-300 font-mono text-xs">{p.invoices?.invoice_number ?? "—"}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs capitalize">{p.method?.replace?.("_", " ")}</td>
                <td className="px-4 py-3 text-zinc-300 text-xs capitalize">{p.category ?? p.kind}</td>
                <td className={`px-4 py-3 text-right ${(p.category ?? p.kind) === "refund" ? "text-red-300" : "text-emerald-300"}`}>{(p.category ?? p.kind) === "refund" ? "-" : ""}{fmtMoney(p.amount)}</td>
                <td className="px-4 py-3 text-zinc-500 text-xs">{p.reference ?? ""}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}